import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Card, Col, Descriptions, Grid, Row} from 'antd';
import {observer} from 'mobx-react-lite';
import {ReservationState} from 'store/reservations/ReservationState';
import {Shell} from 'components/Shell';
import {AppRoutePaths} from 'routing/Routes';
import {CardTitle, DescriptionLabel} from 'views/serviceActions/utils/ServicesUtils';
import {getIsMobile, getIsTablet, renderDate} from 'helpers/utils';

export const ReservationDisplayView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [state] = useState(() => new ReservationState());
  const {reservationId} = useParams();

  const screen = Grid.useBreakpoint();
  const isMobile = useMemo(() => getIsMobile(screen), [screen]);
  const isTablet = useMemo(() => getIsTablet(screen), [screen]);

  useEffect(() => {
    if (reservationId !== undefined && state.reservation.id === undefined) {
      state.loadReservation(Number(reservationId));
    }
  }, [state]);

  return (
    <Shell
      backLink
      title={
        <>
          {t('Reservation: ')}
          {`${state.reservation.car?.carMakeName} ${state.reservation.car?.carModelName} (${state.reservation.car?.plateNumber})`}
        </>
      }
      actionButtons={[
        <Button
          key="edit"
          size="large"
          className="shell-btn"
          onClick={() => navigate(`${AppRoutePaths.reservation.url}/${reservationId}/edit`)}
        >
          {t('Edit Reservation')}
        </Button>,
      ]}
    >
      <Row gutter={16}>
        <Col className="flex flex-col gap-4" span={isMobile ? 24 : 10}>
          <Card title={<CardTitle text={t('Details')} />}>
            <Descriptions column={isTablet ? 1 : 2} colon={false} layout="vertical">
              <Descriptions.Item label={<DescriptionLabel text={t('Begins')} />}>
                {renderDate(true, state.reservation.begins)}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Ends')} />}>
                {renderDate(true, state.reservation.ends)}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Customer')} />}>
                {state.reservation.customer?.companyName ??
                  `${state.reservation.customer?.firstName} ${state.reservation.customer?.lastName}`}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Created by')} />}>
                {state.reservation.createdBy}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Creation date')} />} span={isTablet ? 1 : 2}>
                {renderDate(false, state.reservation.createdDate)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        {state.reservation.description && (
          <Col span={isMobile ? 24 : 14} className="mt-4 md:mt-0">
            <Card title={<CardTitle text={t('Description')} />}>{state.reservation.description}</Card>
          </Col>
        )}
      </Row>
    </Shell>
  );
});
